/**
 * ClonotypePlot — single network-graph card for one clonotype.
 *
 * Extracted from NetworkMapVisualization.tsx (SSTSW-857) — the code here is the
 * same component that previously lived inline inside that file. Moving it to
 * its own module lets it be reused in two places:
 *   1. The big NxN grid on the Antibody (Hit) selection page (NetworkMapVisualization).
 *   2. The per-row Network Map column inside the AB table (OuterClonotypeTableColumns).
 *
 * Two opt-out props were added during the extraction so the same component can
 * render the smaller, passive variant used inside the table:
 *   - `interactive`           — when false, suppresses single/double-click handlers.
 *   - `showClonotypeIdLabel`  — when false, drops the 16px footer strip so the
 *                                chart uses the cell's full height.
 *
 * Behavior is otherwise identical to the original inline definition.
 */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { Box, Flex, Text, Tooltip } from '@mantine/core';
import type * as echarts from 'echarts';
import ReactECharts from 'echarts-for-react';

import type { NetworkMapClonotype, NetworkMapClonotypeNode } from './networkMap.utils';
import { VIS_UNSELECTED_COLOR, getNodeColor, getNodeLegendLabel, getNodeSize } from './networkMap.utils';
import type { SelectedNode } from '../store/slices/clonotypes.slice';
import { addSelectedClonotype, removeSelectedClonotype, selectColorMapBy, setSelectedCells, setSelectedNodes } from '../store/slices/clonotypes.slice';
import type { AppDispatch, AppState } from '../store/store';

interface ChartInnerProps {
  option: any;
  onEvents: Record<string, (event: echarts.ECElementEvent) => void>;
  height: number;
}

function ChartInner({ option, onEvents, height }: ChartInnerProps) {
  return (
    <ReactECharts
      className="clonotype-network-map-chart"
      option={option}
      onEvents={onEvents}
      style={{
        maxHeight: height,
      }}
    />
  );
}

const Chart = React.memo(ChartInner, (prevProps, nextProps) => {
  return prevProps.option === nextProps.option;
});

// Stable empty fallbacks so `?? []` doesn't return a fresh array on every selector call,
// which would trigger react-redux's "returned a different result with same parameters" warning.
const EMPTY_CLONOTYPE_IDS: number[] = [];
const EMPTY_BARCODES: string[] = [];
const EMPTY_NODES: SelectedNode[] = [];

interface ClonotypePlotProps {
  clonotype: NetworkMapClonotype;
  width: number;
  height: number;
  /** Single-click selects/deselects the clonotype. Default true. */
  interactive?: boolean;
  /** Show the clonotype ID in a 16px footer strip. Default true. */
  showClonotypeIdLabel?: boolean;
}

function ClonotypePlotInner({ clonotype, width, height, interactive = true, showClonotypeIdLabel = true }: ClonotypePlotProps) {
  const params = useParams<{ id: string }>();
  const experimentId = params.id as string;

  const dispatch = useDispatch<AppDispatch>();

  const { clonotypeId } = clonotype;

  const selectedClonotypes = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedClonotypes ?? EMPTY_CLONOTYPE_IDS);
  const selectedCells = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedCells ?? EMPTY_BARCODES);
  const selectedNodes = useSelector((state: AppState) => state.clonotypes[experimentId]?.selectedNodes ?? EMPTY_NODES);
  const isSelected = selectedClonotypes.includes(clonotypeId);

  const colorMapBy = useSelector((state: AppState) => selectColorMapBy(state, experimentId));

  const getNodeColorForNode = React.useCallback(
    (node: NetworkMapClonotypeNode) => getNodeColor(node, colorMapBy, clonotype.clonotypeId),
    [clonotype.clonotypeId, colorMapBy],
  );

  const getLegendLabel = React.useCallback(
    (node: NetworkMapClonotypeNode) => getNodeLegendLabel(node, colorMapBy, clonotype.clonotypeId),
    [clonotype.clonotypeId, colorMapBy],
  );

  const option = React.useMemo(() => {
    return {
      series: [
        {
          data: clonotype.nodes.map((node) => ({
            id: node.id,
            name: node.id,
            value: {
              nodeId: node.id,
              cdr3Sequence: node.cdr3Sequence,
              color: getNodeColorForNode(node),
              legendLabel: getLegendLabel(node),
            },
            symbolSize: getNodeSize(node.cells.length) * 2,
            itemStyle: { color: getNodeColorForNode(node), borderColor: 'rgba(0,0,0,0.2)' },
            x: node.x * 100,
            y: node.y * 100,
          })),
          links: clonotype.links,
          lineStyle: {
            opacity: clonotype.links.length > 300 ? 0.05 : clonotype.links.length > 200 ? 0.1 : 0.2,
            color: isSelected ? 'rgba(150,150,150)' : VIS_UNSELECTED_COLOR,
          },
          type: 'graph',
          layout: 'none',
          force: {
            repulsion: 30,
            edgeLength: [20, 60],
            simulation: false,
          },
          roam: false,
          label: {
            show: false,
          },
          emphasis: {
            focus: 'none',
            label: {
              show: false,
            },
          },
        },
      ],
    };
  }, [clonotype.links, clonotype.nodes, getLegendLabel, getNodeColorForNode, isSelected]);

  const handleClickClonotype = React.useCallback(() => {
    const barcodesForThisClonotype: string[] = clonotype.nodes.flatMap((node) => node.cells.map((cell) => cell.barcode));

    if (selectedClonotypes.includes(clonotypeId)) {
      dispatch(removeSelectedClonotype({ experimentId, clonotypeId }));
      dispatch(setSelectedCells({ experimentId, cells: selectedCells.filter((b) => !barcodesForThisClonotype.includes(b)) }));
      dispatch(setSelectedNodes({ experimentId, nodes: selectedNodes.filter((n) => n.clonotypeId !== clonotypeId) }));
    } else {
      dispatch(addSelectedClonotype({ experimentId, clonotypeId }));
      dispatch(setSelectedCells({ experimentId, cells: [...selectedCells, ...barcodesForThisClonotype] }));
      dispatch(
        setSelectedNodes({
          experimentId,
          nodes: [
            ...selectedNodes,
            ...clonotype.nodes.map((node) => ({
              clonotypeId,
              nodeId: parseInt(node.id, 10),
              cellIds: node.cells.map((cell) => cell.cellId as number),
            })),
          ],
        }),
      );
    }
  }, [clonotypeId, dispatch, experimentId, selectedClonotypes, selectedCells, selectedNodes, clonotype.nodes]);

  const { cdrh3AaLen, vGSegment } = React.useMemo(() => {
    const anyNode = clonotype.nodes[0];
    const anyCell = anyNode?.cells[0];
    return {
      cdrh3AaLen: anyCell?.cdrh3AaLen ?? undefined,
      vGSegment: `${anyCell?.irVdj1VCall?.split('*')[0] ?? ''}/${anyCell?.irVdj1JCall}`,
    };
  }, [clonotype.nodes]);

  const clonotypeTooltipInfo = React.useMemo(
    () => ({
      clonotypeId: clonotype.clonotypeId,
      numberOfNodes: clonotype.nodes.length,
      clonotypeSize: clonotype.nodes.reduce((acc, node) => acc + node.cells.length, 0),
      vgSegment: vGSegment,
      cdrh3AaLen,
    }),
    [cdrh3AaLen, clonotype.clonotypeId, clonotype.nodes, vGSegment],
  );

  const [tooltipState, setTooltipState] = React.useState<
    | {
        type: 'clonotype';
        clonotypeId: number;
        numberOfNodes: number;
        clonotypeSize: number;
        vgSegment: string | undefined;
        cdrh3AaLen: number | undefined;
      }
    | {
        type: 'node';
        clonotypeId: number;
        numberOfCells: number;
        vgSegment: string | undefined;
        cdrh3AaLen: number | undefined;
        nodeId: string;
        cdr3Sequence: string;
        color: string;
        legendLabel: string;
      }
  >({
    ...clonotypeTooltipInfo,
    type: 'clonotype',
  });

  const tooltipContent = React.useMemo(() => {
    if (tooltipState.type === 'clonotype') {
      return (
        <Box>
          <Text size="sm">Clonotype ID: {tooltipState.clonotypeId}</Text>
          <Text size="sm">Number of nodes: {tooltipState.numberOfNodes}</Text>
          <Text size="sm">Clonotype size: {tooltipState.clonotypeSize}</Text>
          <Text size="sm">V-J gene segment: {tooltipState.vgSegment}</Text>
          <Text size="sm">CDR3 length: {tooltipState.cdrh3AaLen}</Text>
        </Box>
      );
    }
    return (
      <Box>
        <Text size="sm">Clonotype ID: {tooltipState.clonotypeId}</Text>
        <Text size="sm">Number of cells: {tooltipState.numberOfCells}</Text>
        <Text size="sm">V-J gene segment: {tooltipState.vgSegment}</Text>
        <Text size="sm">CDR3 length: {tooltipState.cdrh3AaLen}</Text>
        <Text size="sm">CDR3: {tooltipState.cdr3Sequence}</Text>
        {tooltipState.legendLabel && (
          <Flex align="center" gap={5}>
            <Box
              w={10}
              h={10}
              bg={tooltipState.color}
              mt={-3}
              style={{
                borderRadius: '50%',
              }}
            />
            <Text size="sm">{tooltipState.legendLabel}</Text>
          </Flex>
        )}
      </Box>
    );
  }, [tooltipState]);

  const onEvents: Record<string, (event: echarts.ECElementEvent) => void> = {
    mouseover: (event) => {
      if (event.dataType === 'node') {
        if (!event.data) {
          return;
        }
        if (typeof event.data !== 'object' || !('value' in event.data)) {
          return;
        }
        const { nodeId, cdr3Sequence, color, legendLabel } = event.data.value as unknown as {
          nodeId: string;
          cdr3Sequence: string;
          color: string;
          legendLabel: string;
        };
        setTooltipState({
          ...clonotypeTooltipInfo,
          numberOfCells: clonotype.nodes.find((node) => node.id === nodeId)?.cells.length ?? 0,
          type: 'node',
          nodeId,
          cdr3Sequence,
          color,
          legendLabel,
        });
      } else {
        setTooltipState({
          ...clonotypeTooltipInfo,
          type: 'clonotype',
        });
      }
    },
    mouseout: (event) => {
      if (event.dataType === 'node') {
        setTooltipState({
          ...clonotypeTooltipInfo,
          type: 'clonotype',
        });
      }
    },
  };

  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setMounted(true);
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  const handleSingleClick = React.useCallback(() => {
    if (!interactive) {
      return;
    }
    handleClickClonotype();
  }, [interactive, handleClickClonotype]);

  if (!mounted) {
    return null;
  }

  const chartAreaHeight = showClonotypeIdLabel ? height - 16 : height;

  return (
    <Tooltip.Floating multiline position="bottom-end" label={tooltipContent} disabled={!tooltipState} classNames={{ tooltip: 'help-tooltip' }}>
      <Box
        w={width}
        h={height}
        onClick={interactive ? handleSingleClick : undefined}
        style={{
          border: isSelected ? '1px solid #0064B0' : '0.5px solid #f0f0f0',
          backgroundColor: isSelected ? '#E2F2FF' : undefined,
          display: 'flex',
          flexDirection: 'column',
          cursor: interactive ? 'pointer' : 'default',
        }}
        data-testid={`clonotype-plot${isSelected ? '-selected' : '-unselected'}`}
      >
        <Box style={{ flex: 1, minHeight: 0 }}>
          <Chart option={option} onEvents={onEvents} height={chartAreaHeight} />
        </Box>
        {showClonotypeIdLabel && (
          <Text size="xs" c="dimmed" fw={500} ta="right" pr={8} style={{ lineHeight: '16px', pointerEvents: 'none' }}>
            {clonotypeId}
          </Text>
        )}
      </Box>
    </Tooltip.Floating>
  );
}

export const ClonotypePlot = React.memo(ClonotypePlotInner);
